import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import SpinnerDark from '../UI/SpinnerDark';
import {
    cancelOrder,
    checkOrderPayment,
    checkOrderPaymentAdmin,
    fetchOrderById,
    fetchOrderByIdAdmin,
    payForOrder,
} from '../../http/ordersAPI';
import {observer} from 'mobx-react-lite';
import {Context} from '../../index';
import {
    CONTACT_EMAIL,
    ORDER_DELIVERY_YANDEX_PICKUP,
    ORDER_PAYMENT_RETURN_PARAM,
    ORDER_PAYMENT_RETURN_VALUE,
    PAYMENT_CHECK_ABNORMAL,
    PAYMENT_CHECK_CANCELED,
    PAYMENT_CHECK_PAID,
    PAYMENT_CHECK_PENDING,
    ROLE_ADMIN,
    TELEGRAM_URL,
    normalizeOrderStatusCode,
    orderDeliveryPresentation,
    orderStatusPresentation,
} from '../../util/Constants';
import OrderItems from './OrderItems';
import ShipmentCard from './ShipmentCard';
import OrderControlForm from '../admin/OrderControlForm';
import OrderStatusTimeline from './OrderStatusTimeline';
import {formatOrderDate} from '../../util/formatOrderDate';
import {absolutePageUrl} from '../../util/siteUrl';

const DB_CREATED = 'CREATED';
const PAYMENT_RETRY_AFTER_HEADER = 'x-rate-limit-retry-after-seconds';

function paymentRetryDelayMs(err) {
    const raw = err?.response?.headers?.[PAYMENT_RETRY_AFTER_HEADER];
    const sec = Number(raw);
    if (Number.isFinite(sec) && sec > 0) {
        return sec * 1000;
    }
    return 60_000;
}

function orderStatusDotClass(status) {
    const s = normalizeOrderStatusCode(status);
    if (s === 'CANCELED') return 'bg-red-500 ring-2 ring-red-300';
    if (s === 'CREATED') return 'bg-amber-400 ring-2 ring-amber-200 animate-pulse';
    if (s === 'PAID') return 'bg-pine-500 ring-2 ring-pine-300';
    if (s === 'SHIPPED') return 'bg-pine-600 ring-2 ring-pine-400';
    if (s === 'DELIVERED') return 'bg-pine-800 ring-2 ring-pine-600';
    return 'bg-stone-400 ring-2 ring-stone-300';
}

const Order = observer(() => {
    const {user} = useContext(Context);
    const {id} = useParams();
    const {pathname} = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [order, setOrder] = useState({});
    const [isLoading, setLoading] = useState(true);
    const [isPaid, setIsPaid] = useState(0);
    const [feedback, setFeedback] = useState('');
    /** URL на оплату, если окно не открылось — показываем кликабельной ссылкой */
    const [paymentFallbackUrl, setPaymentFallbackUrl] = useState('');
    const [paymentRateLimitedUntilMs, setPaymentRateLimitedUntilMs] = useState(0);
    /** Вернулись с экрана оплаты (return_url / ?paymentReturn=1) */
    const [fromPaymentCallback, setFromPaymentCallback] = useState(false);
    /** Прошла минута, статус оплаты по-прежнему неизвестен */
    const [paymentVerifySlow, setPaymentVerifySlow] = useState(false);
    /** Ответ checkPayment: неуспех у провайдера, не pending — показываем пояснение */
    const [paymentStatusNotice, setPaymentStatusNotice] = useState('');
    const fastVerifyEndRef = useRef(null);
    const returnParamHandledRef = useRef(false);
    const isAdmin = user.userRole === ROLE_ADMIN;

    const loadOrder = useCallback(
        (silent = false) => {
            if (!silent) {
                setLoading(true);
            }
            const req = isAdmin ? fetchOrderByIdAdmin(id) : fetchOrderById(id, user.isAuth);
            return req
                .then((data) => {
                    setOrder(data);
                    const s = normalizeOrderStatusCode(data.status);
                    if (s !== DB_CREATED) {
                        setPaymentFallbackUrl('');
                        setFeedback((prev) => {
                            if (
                                prev.includes('Если окно не открылось') ||
                                prev.includes('ссылке ниже') ||
                                prev.includes('Запрос отправлен')
                            ) {
                                return '';
                            }
                            return prev;
                        });
                    }
                })
                .catch((err) =>
                    setFeedback(err.response?.data?.message || err.message || 'Ошибка загрузки заказа'),
                )
                .finally(() => {
                    if (!silent) {
                        setLoading(false);
                    }
                });
        },
        [id, user.isAuth, isAdmin],
    );

    useEffect(() => {
        const s = normalizeOrderStatusCode(order.status);
        if (s === 'PAID' || s === 'SHIPPED' || s === 'DELIVERED') {
            setIsPaid(1);
        } else if (s === 'CANCELED') {
            setIsPaid(-1);
        }
        if (s !== DB_CREATED) {
            setPaymentStatusNotice('');
        }
    }, [order.status]);

    const runPaymentCheck = useCallback(() => {
        if (Date.now() < paymentRateLimitedUntilMs) {
            return Promise.resolve();
        }
        const run = (data) => {
            const code = typeof data === 'string' ? data.trim() : data;
            if (code === PAYMENT_CHECK_PAID) {
                setIsPaid(1);
                setPaymentStatusNotice('');
            } else if (code === PAYMENT_CHECK_CANCELED) {
                // Платёж в банке отменён/неуспешен; заказ обычно остаётся CREATED — не путаем с отменой самого заказа
                setPaymentStatusNotice(
                    'Предыдущая попытка оплаты в банке не завершена (платёж отменён или отклонён). Заказ оформлен, его можно оплатить снова — нажмите «Перейти к оплате».',
                );
            } else if (code === PAYMENT_CHECK_PENDING) {
                setPaymentStatusNotice('');
            } else if (code === PAYMENT_CHECK_ABNORMAL) {
                setPaymentStatusNotice(
                    'По данным платёжной системы оплата в нестандартном статусе (не ожидает оплаты). Заказ ещё не оплачен. Можно снова нажать «Перейти к оплате» или «Обновить статус оплаты» позже. Если списание прошло, но статус не меняется — напишите нам.',
                );
            } else {
                setPaymentStatusNotice('');
            }
            loadOrder(true);
        };
        if (isAdmin) {
            return checkOrderPaymentAdmin(id).then(run).catch((err) => {
                if (err?.response?.status === 429) {
                    const delayMs = paymentRetryDelayMs(err);
                    setPaymentRateLimitedUntilMs(Date.now() + delayMs);
                    setFeedback('Слишком частая проверка оплаты. Подождите немного и попробуйте снова.');
                }
                throw err;
            });
        }
        return checkOrderPayment(id, user.isAuth).then(run).catch((err) => {
            if (err?.response?.status === 429) {
                const delayMs = paymentRetryDelayMs(err);
                setPaymentRateLimitedUntilMs(Date.now() + delayMs);
                setFeedback('Слишком частая проверка оплаты. Подождите немного и попробуйте снова.');
            }
            throw err;
        });
    }, [id, user.isAuth, isAdmin, loadOrder, paymentRateLimitedUntilMs]);

    const pay = () => {
        setPaymentStatusNotice('');
        payForOrder(id, user.isAuth)
            .then((response) => {
                const url = typeof response === 'string' ? response.trim() : '';
                setPaymentFallbackUrl(url);
                setFeedback(
                    url
                        ? 'Если окно не открылось — откройте оплату по ссылке ниже.'
                        : 'Запрос отправлен. Если окно не открылось, нажмите «Перейти к оплате» ещё раз.',
                );
            })
            .catch((err) => setFeedback(err.response?.data?.message || err.message));
    };

    const check = () => {
        runPaymentCheck().catch((err) => setFeedback(err.response?.data?.message || err.message));
    };

    const cancel = () => {
        if (!window.confirm('Отменить заказ? Это действие нельзя отменить.')) {
            return;
        }
        cancelOrder(id, user.isAuth)
            .then(() => {
                setIsPaid(-1);
                setFeedback('');
                setPaymentStatusNotice('');
                setPaymentFallbackUrl('');
                return loadOrder(true);
            })
            .catch((err) => setFeedback(err.response?.data?.message || err.message));
    };

    useEffect(() => {
        loadOrder(false);
    }, [loadOrder]);

    useEffect(() => {
        if (returnParamHandledRef.current) {
            return;
        }
        if (searchParams.get(ORDER_PAYMENT_RETURN_PARAM) !== ORDER_PAYMENT_RETURN_VALUE) {
            return;
        }
        returnParamHandledRef.current = true;
        setFromPaymentCallback(true);
        fastVerifyEndRef.current = Date.now() + 60_000;
        navigate({pathname, search: ''}, {replace: true});
    }, [searchParams, navigate, pathname]);

    useEffect(() => {
        if (!fromPaymentCallback || !fastVerifyEndRef.current) {
            return undefined;
        }
        if (normalizeOrderStatusCode(order.status) !== DB_CREATED) {
            setPaymentVerifySlow(false);
            return undefined;
        }
        const end = fastVerifyEndRef.current;
        const left = end - Date.now();
        if (left <= 0) {
            setPaymentVerifySlow(true);
            return undefined;
        }
        const t = window.setTimeout(() => {
            setPaymentVerifySlow(true);
        }, left);
        return () => window.clearTimeout(t);
    }, [fromPaymentCallback, order.status]);

    useEffect(() => {
        if (normalizeOrderStatusCode(order.status) !== DB_CREATED) {
            return undefined;
        }
        let cancelled = false;
        let timeoutId;

        const onReturnToPage = () => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
                return;
            }
            loadOrder(true);
            runPaymentCheck().catch(() => {});
        };

        const runIfVisible = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
                loadOrder(true);
                runPaymentCheck().catch(() => {});
            }
        };
        const tickLoop = () => {
            if (cancelled) {
                return;
            }
            const fast =
                fromPaymentCallback &&
                fastVerifyEndRef.current != null &&
                Date.now() < fastVerifyEndRef.current;
            const delay = fast ? 2000 : 5000;
            timeoutId = window.setTimeout(() => {
                if (cancelled) {
                    return;
                }
                runIfVisible();
                tickLoop();
            }, delay);
        };

        runIfVisible();
        tickLoop();
        document.addEventListener('visibilitychange', onReturnToPage);
        window.addEventListener('pageshow', onReturnToPage);

        return () => {
            cancelled = true;
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
            document.removeEventListener('visibilitychange', onReturnToPage);
            window.removeEventListener('pageshow', onReturnToPage);
        };
    }, [order.status, fromPaymentCallback, loadOrder, runPaymentCheck]);

    if (isLoading) {
        return <SpinnerDark />;
    }

    const st = orderStatusPresentation(order.status);
    const statusNorm = normalizeOrderStatusCode(order.status);
    const del = orderDeliveryPresentation(order.deliveryType);
    const itemsSubtotal = (order.items || []).reduce(
        (s, i) => s + Number(i.price) * (i.itemCount ?? 0),
        0,
    );
    const deliveryFeeNum = Number(order.deliveryFee ?? 0);

    const showPaidTotal =
        statusNorm !== 'CREATED' && statusNorm !== 'CANCELED' && statusNorm !== '';

    const telegramHref = TELEGRAM_URL.trim() || 'https://t.me/lutica_candle_shop';
    const pageUrlForEmail =
        typeof window !== 'undefined' ? window.location.href : absolutePageUrl(pathname);
    const supportMailto = `mailto:${CONTACT_EMAIL}?${new URLSearchParams({
        subject: `Заказ ${order.number ?? id}`,
        body: `Ссылка на эту страницу: ${pageUrlForEmail}\n\n`,
    }).toString()}`;

    return (
        <div>
            <div className="lut-card">
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-4">
                    <div className="min-w-0">
                        <div className="flex min-w-0 flex-wrap items-center gap-3">
                            <h1 className="text-2xl font-semibold text-bark-900">Заказ {order.number}</h1>
                            <div
                                className="inline-flex max-w-full items-center gap-2 rounded-full border border-stone-200 bg-cream-100/90 py-1 pl-2 pr-3 text-sm shadow-sm"
                                title={st.translation}
                            >
                                <span
                                    className={`inline-block h-3.5 w-3.5 shrink-0 rounded-full ${orderStatusDotClass(order.status)}`}
                                    aria-hidden
                                />
                                <span className="font-medium text-bark-800">
                                    <span className="text-bark-500">Статус: </span>
                                    {st.translation}
                                </span>
                            </div>
                        </div>
                        {order.dateTime != null && (
                            <p className="mt-2 text-sm text-bark-500">
                                Дата заказа: {formatOrderDate(order.dateTime)}
                            </p>
                        )}
                    </div>
                </div>

                {fromPaymentCallback && statusNorm === DB_CREATED && isPaid !== 1 && (
                    <div className="mt-4 flex items-start gap-3 rounded-2xl border border-pine-200/80 bg-pine-50/90 px-3 py-3 sm:px-4">
                        <span
                            className="mt-0.5 inline-block h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-pine-600 border-t-transparent"
                            aria-hidden
                        />
                        <p className="text-sm text-bark-800">
                            Проверяем оплату… Статус обновится на этой странице, как только платёжная система
                            подтвердит платёж.
                        </p>
                    </div>
                )}

                {paymentVerifySlow && statusNorm === DB_CREATED && isPaid !== 1 && (
                    <div
                        className={`rounded-2xl border border-amber-200 bg-amber-50/90 px-3 py-3 text-sm text-bark-800 sm:px-4 ${
                            fromPaymentCallback ? 'mt-3' : 'mt-4'
                        }`}
                    >
                        <p>
                            Пока не удаётся получить статус оплаты. Подождите ещё немного — подтверждение
                            иногда задерживается.
                        </p>
                        <p className="mt-2">
                            Если ситуация не сменится, напишите нам в{' '}
                            <a
                                href={telegramHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-pine-800 underline decoration-pine-500/50 underline-offset-2 hover:text-pine-950"
                            >
                                Telegram
                            </a>
                            {' '}
                            или на{' '}
                            <a
                                href={supportMailto}
                                className="font-medium text-pine-800 underline decoration-pine-500/50 underline-offset-2 hover:text-pine-950"
                            >
                                почту
                            </a>
                            — укажите ссылку на эту страницу.
                        </p>
                    </div>
                )}

                {paymentStatusNotice && statusNorm === DB_CREATED && isPaid !== 1 && (
                    <div
                        className={`rounded-2xl border border-sky-200/90 bg-sky-50/90 px-3 py-3 text-sm text-bark-800 sm:px-4 ${
                            fromPaymentCallback || paymentVerifySlow ? 'mt-3' : 'mt-4'
                        }`}
                    >
                        <p>{paymentStatusNotice}</p>
                    </div>
                )}

                <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
                    <div className="min-w-0 flex-1">
                        <OrderStatusTimeline status={order.status} />
                    </div>
                    {isAdmin && statusNorm !== 'CANCELED' && (
                        <div className="shrink-0 space-y-2">
                            <h2 className="text-sm font-semibold uppercase tracking-wide text-bark-500">
                                Панель администратора
                            </h2>
                            <OrderControlForm
                                id={id}
                                setIsPaid={setIsPaid}
                                setOrder={setOrder}
                                orderStatus={order.status}
                                setFeedback={setFeedback}
                                reloadOrder={() => loadOrder(true)}
                            />
                        </div>
                    )}
                </div>

                {(!!feedback || paymentFallbackUrl) && (
                    <div className="mt-4 rounded-2xl bg-mustard-100/80 px-3 py-2 text-sm text-bark-800">
                        {!!feedback && <p>{feedback}</p>}
                        {paymentFallbackUrl && (
                            <p className={feedback ? 'mt-2' : ''}>
                                <a
                                    href={paymentFallbackUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block break-all font-medium text-pine-800 underline decoration-pine-600/50 underline-offset-2 hover:text-pine-950"
                                >
                                    {paymentFallbackUrl}
                                </a>
                            </p>
                        )}
                    </div>
                )}

                {isPaid !== 1 && statusNorm === DB_CREATED && (
                    <div className="mt-6 space-y-3">
                        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                            <button
                                type="button"
                                onClick={pay}
                                className="lut-btn-pine min-h-[3rem] flex-1 px-6 text-base font-semibold shadow-md ring-2 ring-pine-800/15 sm:min-w-[14rem] sm:flex-none"
                            >
                                Перейти к оплате
                            </button>
                            <button
                                type="button"
                                onClick={check}
                                className="min-h-[3rem] flex-1 rounded-2xl border-2 border-pine-700 bg-white px-6 text-base font-semibold text-pine-900 shadow-sm hover:bg-cream-50 sm:min-w-[12rem] sm:flex-none"
                            >
                                Обновить статус оплаты
                            </button>
                            <button
                                type="button"
                                onClick={cancel}
                                className="min-h-[3rem] flex-1 rounded-2xl border-2 border-red-400 bg-red-50 px-6 text-base font-semibold text-red-900 hover:bg-red-100 sm:min-w-[12rem] sm:flex-none"
                            >
                                Отменить заказ
                            </button>
                        </div>
                        <p className="text-xs leading-relaxed text-bark-500">
                            После оплаты банк и платёжная система подтверждают платёж не мгновенно — статус на
                            странице обновится сам. Обычно это занимает до минуты, иногда дольше.
                        </p>
                    </div>
                )}

                <div className="mt-8 grid gap-8 lg:grid-cols-2 lg:items-start lg:gap-10">
                    <div className="space-y-6">
                        <div>
                            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-bark-500">
                                Получатель
                            </h2>
                            <ShipmentCard shipment={order.shipmentResponse} />
                        </div>
                        {!!order.commentary && (
                            <div>
                                <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-bark-500">
                                    Комментарий к заказу
                                </h2>
                                <div className="rounded-2xl border border-stone-200/80 bg-cream-50/80 px-4 py-3 text-sm text-bark-800">
                                    <p className="whitespace-pre-wrap">{order.commentary}</p>
                                </div>
                            </div>
                        )}
                        <div>
                            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-bark-500">
                                Способ доставки
                            </h2>
                            <div className="rounded-2xl border border-stone-200/80 bg-cream-50/80 px-4 py-3 text-sm text-bark-800">
                                {order.deliveryType ? (
                                    <p className="font-medium">{del.label}</p>
                                ) : (
                                    <p className="text-bark-500">—</p>
                                )}
                                {isAdmin &&
                                    order.deliveryType === ORDER_DELIVERY_YANDEX_PICKUP &&
                                    order.yandexPickupPointId && (
                                        <p className="mt-2 text-xs text-bark-500">
                                            ID пункта (Яндекс НДД): {order.yandexPickupPointId}
                                        </p>
                                    )}
                            </div>
                        </div>
                    </div>

                    <div>
                        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-bark-500">Товары</h2>
                        <OrderItems items={order.items} />
                        <div className="mt-4 space-y-1.5 border-t border-stone-200/80 pt-4 text-sm text-bark-700">
                            {(order.items || []).length > 0 && (
                                <>
                                    <p className="text-right">Сумма по товарам: {itemsSubtotal.toFixed(2)} ₽</p>
                                    {order.deliveryType === ORDER_DELIVERY_YANDEX_PICKUP && deliveryFeeNum > 0 && (
                                        <p className="text-right">Доставка до ПВЗ: {deliveryFeeNum.toFixed(2)} ₽</p>
                                    )}
                                </>
                            )}
                            {showPaidTotal ? (
                                <p className="text-right text-base font-semibold text-pine-900">Оплачено: {order.price} ₽</p>
                            ) : (
                                <p className="text-right text-base font-semibold text-bark-900">
                                    Итого к оплате: {order.price} ₽
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default Order;
